import { useQuery } from '@tanstack/react-query';
import { type KeyboardEvent, type ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type MentionCandidate } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { collaborationApi, collaborationKeys } from '../api';

/**
 * Ô soạn bình luận với gợi ý `@`.
 *
 * Gõ `@` mở danh sách thành viên project; chọn một người sẽ chèn markup
 * `@[Tên](uuid)` vào văn bản. Người dùng thấy `@Tên`, còn thứ được lưu là
 * markup có id — nhờ vậy đổi tên hiển thị thì bình luận cũ vẫn trỏ đúng người.
 */
export function CommentComposer({
  projectId,
  disabled,
  onSubmit,
}: {
  projectId: string;
  disabled?: boolean;
  onSubmit: (body: string) => Promise<void>;
}): ReactNode {
  const { t } = useTranslation();
  const [body, setBody] = useState('');
  const [query, setQuery] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);

  const candidates = useQuery({
    queryKey: collaborationKeys.mentionCandidates(projectId),
    queryFn: () => collaborationApi.mentionCandidates(projectId),
    staleTime: 5 * 60_000,
    enabled: query !== null,
  });

  const matches = (candidates.data ?? [])
    .filter((c) => {
      if (!query) return true;
      // Lọc theo cả phòng ban: gõ `@kỹ` tìm được cả phòng Kỹ thuật — đó chính
      // là lý do phòng ban có mặt ở đây (APM-1315).
      const needle = query.toLowerCase();
      return (
        c.displayName.toLowerCase().includes(needle) ||
        (c.departmentName?.toLowerCase().includes(needle) ?? false)
      );
    })
    .slice(0, 6);

  /** Bắt `@` đang gõ dở ở ngay trước con trỏ. */
  function refreshQuery(value: string, caret: number): void {
    const before = value.slice(0, caret);
    const at = before.lastIndexOf('@');
    // `@` phải đứng đầu dòng hoặc sau khoảng trắng, và chưa có khoảng trắng nào
    // sau nó — nếu không thì `email@example.com` cũng mở gợi ý.
    if (at === -1 || (at > 0 && !/\s/.test(before[at - 1]!))) return setQuery(null);
    const fragment = before.slice(at + 1);
    if (/\s/.test(fragment)) return setQuery(null);
    setQuery(fragment);
  }

  function insertMention(candidate: MentionCandidate): void {
    const el = textarea.current;
    if (!el) return;

    const caret = el.selectionStart;
    const before = body.slice(0, caret);
    const at = before.lastIndexOf('@');
    const next = `${body.slice(0, at)}@[${candidate.displayName}](${candidate.userId}) ${body.slice(caret)}`;

    setBody(next);
    setQuery(null);
    // Đưa con trỏ về sau đoạn vừa chèn, nếu không nó nhảy về cuối và người
    // dùng phải tự tìm lại chỗ đang viết.
    const position = at + `@[${candidate.displayName}](${candidate.userId}) `.length;
    queueMicrotask(() => {
      el.focus();
      el.setSelectionRange(position, position);
    });
  }

  async function submit(): Promise<void> {
    const trimmed = body.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onSubmit(trimmed);
      setBody('');
      setQuery(null);
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === 'Escape' && query !== null) {
      event.preventDefault();
      setQuery(null);
      return;
    }
    // Ctrl/Cmd+Enter để gửi. Enter trần vẫn xuống dòng — bình luận nhiều dòng
    // là chuyện thường, và gửi nhầm vì Enter thì không rút lại được.
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void submit();
    }
  }

  return (
    <div className="relative">
      <textarea
        ref={textarea}
        value={body}
        disabled={disabled || busy}
        rows={3}
        placeholder={t('collaboration.commentPlaceholder')}
        aria-label={t('collaboration.commentPlaceholder')}
        className="w-full resize-y rounded-DEFAULT border border-border bg-background px-3 py-2 text-sm"
        onChange={(event) => {
          setBody(event.target.value);
          refreshQuery(event.target.value, event.target.selectionStart);
        }}
        onKeyDown={onKeyDown}
      />

      {query !== null && matches.length > 0 && (
        <ul
          role="listbox"
          aria-label={t('collaboration.mentionSuggestions')}
          className="absolute bottom-full z-10 mb-1 w-64 overflow-hidden rounded-DEFAULT border border-border bg-background shadow-lg"
        >
          {matches.map((candidate) => (
            <li key={candidate.userId}>
              <button
                type="button"
                className={cn(
                  'flex w-full flex-col items-start px-3 py-1.5 text-left text-sm',
                  'hover:bg-muted',
                )}
                onClick={() => insertMention(candidate)}
              >
                <span>{candidate.displayName}</span>
                <span className="text-xs text-muted-foreground">
                  {candidate.departmentName
                    ? `${candidate.email} · ${candidate.departmentName}`
                    : candidate.email}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" disabled={!body.trim() || busy || disabled} onClick={() => void submit()}>
          {t('collaboration.send')}
        </Button>
        <span className="text-xs text-muted-foreground">{t('collaboration.sendHint')}</span>
      </div>
    </div>
  );
}
